import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, useUser } from '@clerk/clerk-react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Container,
} from '@mui/material';
import { API_BASE_URL } from '../types';

// Define the structure of the statistics data expected from the API
interface DailyStat {
  scan_date: string; // Dates usually come as strings from JSON
  match_found_count: number;
  no_match_found_count: number;
  total_scans: number;
}

const Statistics: React.FC = () => {
  const { getToken } = useAuth();
  const { user } = useUser(); // Get user information
  const [stats, setStats] = useState<DailyStat[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Calculate dates for the last 30 days
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 29); // Go back 29 days to include today (total 30 days)

    // Format dates as YYYY-MM-DD for the API query
    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const startDateString = formatDate(startDate);
    const endDateString = formatDate(endDate);

    const fetchStats = async () => { // fetchStats now defined after date strings
      setIsLoading(true);
      setError(null);
      try {
        const token = await getToken({ template: "covervision-backend" });
        if (!token) {
          throw new Error('Authentication token not available.');
        }

        // Fetch stats (defaulting to last 7 days as per backend)
        const response = await axios.get(`${API_BASE_URL}/stats/me/daily`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          // Add query parameters for the date range
          params: {
            start_date: startDateString,
            end_date: endDateString,
          }
        });

        setStats(response.data);
      } catch (err: any) {
        console.error('Error fetching statistics:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to fetch statistics.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats(); // Call fetchStats without arguments
  }, [getToken]); // Dependency array includes getToken

  const userEmail = user?.primaryEmailAddress?.emailAddress || 'Loading...';

  return (
    <Container maxWidth="md"> {/* Constrain width for better readability */}
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
          Your Daily Scan Statistics
        </Typography>
        <Typography variant="h6" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 3, color: 'text.secondary' }}>
          User: {userEmail}
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : stats.length === 0 ? (
           <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
             No scan statistics found for the last 30 days.
           </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table sx={{ minWidth: 650 }} aria-label="scan statistics table">
              <TableHead sx={{ backgroundColor: 'grey.100' }}>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Matches Found</TableCell>
                  <TableCell align="right">No Matches Found</TableCell>
                  <TableCell align="right">Total Scans</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.map((row) => (
                  <TableRow
                    key={row.scan_date}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {new Date(row.scan_date + 'T00:00:00').toLocaleDateString()} {/* Ensure correct date parsing */}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>{row.match_found_count}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>{row.no_match_found_count}</TableCell>
                    <TableCell align="right">{row.total_scans}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default Statistics;
